import type { ResourceType } from "@gettactic/api";
import { useQuery } from "@tanstack/react-query";
import { useTacticHooks } from "../../providers/TacticHooksProvider";
import {
  type CollectedSchedules,
  collectSchedulePages,
  SCHEDULE_PAGE_SIZE
} from "@gettactic/helpers/src/schedules/collectSchedulePages";

export interface UseMySchedulesParams {
  /** Inclusive range start, `yyyy-MM-dd`. */
  start: string;
  /** Inclusive range end, `yyyy-MM-dd`. */
  end: string;
  /** Timezone the range is anchored in. */
  tz: string;
  type?: ResourceType[];
  enabled?: boolean;
}

/**
 * The signed-in user's own reservations across an arbitrary date range.
 *
 * Backed by `/v1/me/schedules`, which matches on slot membership — so a desk
 * somebody booked on this user's behalf is included — and is not limited to a
 * single week the way the agenda is. Pages through the results: this list
 * drives a bulk cancel, so a first page presented as the whole range would
 * cancel an incomplete set.
 */
export const useMySchedules = ({
  start,
  end,
  tz,
  type,
  enabled = true
}: UseMySchedulesParams) => {
  const { apiClient } = useTacticHooks();
  return useQuery({
    queryKey: ["schedules", "mine", start, end, tz, type],
    queryFn: async (): Promise<CollectedSchedules> =>
      collectSchedulePages((cursor) =>
        apiClient.schedules.mine({
          start,
          end,
          tz,
          type,
          limit: SCHEDULE_PAGE_SIZE,
          cursor
        })
      ),
    enabled: enabled && !!start && !!end && !!tz
  });
};
